import React from "react";
import { Shell } from "@/components/Shell";
import { Link } from "react-router-dom";

const EFFECTIVE = "February 1, 2026";

export default function Terms() {
    return (
        <Shell>
            <section className="section-pad">
                <div className="max-w-3xl mx-auto" data-testid="terms-page">
                    <div className="label-base">Legal</div>
                    <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight">Terms of Service</h1>
                    <p className="mt-4 text-sm text-brand-mute">Effective date: {EFFECTIVE}</p>

                    <div className="prose prose-neutral max-w-none mt-10 space-y-8 text-[15px] leading-relaxed text-brand-ink">
                        <p>Welcome to <strong>HealthFairX</strong>. These Terms of Service ("Terms") govern your access to and use of the HealthFairX website and platform (the "Services"). By creating an account or using the Services you agree to these Terms.</p>

                        <div>
                            <h2 className="font-heading text-2xl font-bold mt-2 mb-3">1. The Services</h2>
                            <p>HealthFairX is a B2B marketplace that helps corporate hosts discover and book healthcare and wellness vendors for employee health fairs. Hosts post events and review applications; vendors create profiles and apply to events. HealthFairX is not a healthcare provider and does not provide medical advice.</p>
                        </div>

                        <div>
                            <h2 className="font-heading text-2xl font-bold mt-2 mb-3">2. Accounts</h2>
                            <p>You must provide accurate information when registering and keep it current. You are responsible for activity that happens under your account. You must be 18+ and have authority to bind the organization you represent.</p>
                        </div>

                        <div>
                            <h2 className="font-heading text-2xl font-bold mt-2 mb-3">3. Vendor obligations &amp; booth fees</h2>
                            <p>Vendors set their own service area and categories. When approved by a host, vendors pay a booth fee through our payment processor (Stripe). Pricing is shown before payment. Refunds, if any, are at HealthFairX's discretion and may depend on the host's cancellation policy.</p>
                        </div>

                        <div>
                            <h2 className="font-heading text-2xl font-bold mt-2 mb-3">4. Host obligations</h2>
                            <p>Hosts must accurately describe their event (date, location, attendance, desired categories). Hosts are responsible for vendor selection, on-site logistics, and any health-and-safety obligations at the event venue.</p>
                        </div>

                        <div>
                            <h2 className="font-heading text-2xl font-bold mt-2 mb-3">5. SMS messaging terms</h2>
                            <p>If you opt in to text alerts at <Link to="/sms-opt-in" className="underline">/sms-opt-in</Link>:</p>
                            <ul className="list-disc pl-6 space-y-2 mt-3">
                                <li>You authorize HealthFairX to send you automated text messages about event reminders and vendor application status updates at the mobile number you provide.</li>
                                <li>Consent is not required as a condition of purchasing any goods or services.</li>
                                <li>Message frequency is event-driven (typically a few messages per month).</li>
                                <li>Message and data rates may apply.</li>
                                <li>Reply <strong>HELP</strong> for help, <strong>STOP</strong> to unsubscribe at any time.</li>
                                <li>Supported carriers include AT&amp;T, T-Mobile, Verizon Wireless, Sprint, and most other US carriers. Carriers are not liable for delayed or undelivered messages.</li>
                            </ul>
                        </div>

                        <div>
                            <h2 className="font-heading text-2xl font-bold mt-2 mb-3">6. Acceptable use</h2>
                            <ul className="list-disc pl-6 space-y-2">
                                <li>No fraud, harassment, spam, or illegal conduct.</li>
                                <li>No scraping, reverse engineering, or interference with the Services.</li>
                                <li>No use of the Services to send unsolicited messages or violate telecom laws (including TCPA, CAN-SPAM, and CTIA guidelines).</li>
                            </ul>
                        </div>

                        <div>
                            <h2 className="font-heading text-2xl font-bold mt-2 mb-3">7. Content</h2>
                            <p>You retain ownership of content you upload (logos, photos, descriptions) and grant HealthFairX a non-exclusive license to host, display, and distribute it as needed to operate the Services. You represent that you have the rights to the content you upload.</p>
                        </div>

                        <div>
                            <h2 className="font-heading text-2xl font-bold mt-2 mb-3">8. Intellectual property</h2>
                            <p>HealthFairX and its logo, design, copy, and software are the property of HealthFairX. You may not copy or reuse them without permission.</p>
                        </div>

                        <div>
                            <h2 className="font-heading text-2xl font-bold mt-2 mb-3">9. Disclaimers</h2>
                            <p>The Services are provided "AS IS" without warranties of any kind. HealthFairX does not warrant that any host or vendor will deliver any specific outcome.</p>
                        </div>

                        <div>
                            <h2 className="font-heading text-2xl font-bold mt-2 mb-3">10. Limitation of liability</h2>
                            <p>To the maximum extent permitted by law, HealthFairX's aggregate liability arising out of or relating to the Services is limited to the greater of (a) the amounts paid by you to HealthFairX in the 12 months preceding the claim, or (b) USD $100.</p>
                        </div>

                        <div>
                            <h2 className="font-heading text-2xl font-bold mt-2 mb-3">11. Termination</h2>
                            <p>We may suspend or terminate your account for violation of these Terms or for any conduct that harms the Services or other users. You may stop using the Services at any time.</p>
                        </div>

                        <div>
                            <h2 className="font-heading text-2xl font-bold mt-2 mb-3">12. Governing law</h2>
                            <p>These Terms are governed by the laws of the District of Columbia, United States, without regard to conflict-of-law principles. Venue for any dispute lies in the courts located in the District of Columbia.</p>
                        </div>

                        <div>
                            <h2 className="font-heading text-2xl font-bold mt-2 mb-3">13. Changes</h2>
                            <p>We may update these Terms; the effective date above will be revised and we will provide notice for material changes.</p>
                        </div>

                        <div>
                            <h2 className="font-heading text-2xl font-bold mt-2 mb-3">14. Contact</h2>
                            <p>Questions about these Terms: <a href="mailto:support@healthfairx.com" className="underline" data-testid="terms-contact-email">support@healthfairx.com</a>.<br />HealthFairX — District of Columbia, USA.</p>
                        </div>
                    </div>
                </div>
            </section>
        </Shell>
    );
}
